import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { agentCodes, type AgentCode } from "@patronix/domain";
import { Bot, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { inputClass, primaryButtonClass, secondaryButtonClass, ServerError } from "@/components/Panel";
import { Badge, Card } from "@/components/ui";
import {
  agentErrorMessage,
  agentKeys,
  agentListQuery,
  agentRunsQuery,
  registerAgent,
  updateAgent,
  type AgentRow,
} from "@/features/agents/api";
import { memberListQuery, userKeys } from "@/features/users/api";
import { cn } from "@/lib/cn";
import { useFormat } from "@/lib/format";
import { useCan } from "@/lib/session";
import { isDemoMode } from "@/lib/supabase";

// Settings → Agents (PX-SYS-140, docs/design/agents.md): register an agent
// (a dedicated member without roles), enable it, set its scope, see its last
// runs. Round-trips: agents (a handful of rows), members (for registering),
// 20 runs per agent.

export const Route = createFileRoute("/_app/settings/agents")({
  component: AgentsPage,
});

function AgentsPage() {
  const { t } = useTranslation();
  const canRead = useCan("core.agent.read");
  const agents = useQuery({ ...agentListQuery(), enabled: canRead && !isDemoMode });
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <header className="flex flex-col gap-0.5">
        <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
          {t("nav.settingsGroup.title")} <span aria-hidden="true">/</span> {t("nav.settingsGroup.agents")}
        </nav>
        <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("agents.settingsTitle")}</h1>
        <p className="text-[13px] text-ink-muted">{t("agents.settingsHint")}</p>
        <p className="text-xs text-ink-muted">{t("agents.mfaNote")}</p>
      </header>
      {!canRead ? (
        <p className="text-[13px] text-ink-muted">{t("agents.noPermission")}</p>
      ) : agents.isPending ? (
        <div className="h-40 animate-pulse rounded-2xl bg-surface-muted" aria-hidden="true" />
      ) : (
        agentCodes.map((code) => <AgentCard key={code} code={code} agent={agents.data?.find((a) => a.code === code)} />)
      )}
      <Link to="/planning/suggestions" search={{ status: "proposed" }} className={cn(secondaryButtonClass, "self-start")}>
        {t("agents.openInbox")}
      </Link>
    </div>
  );
}

function AgentCard({ code, agent }: { code: AgentCode; agent: AgentRow | undefined }) {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <Bot size={18} className="text-accent-ink" aria-hidden="true" />
        <h2 className="grow font-semibold">{t(`agents.code.${code}`)}</h2>
        {agent ? (
          <Badge tone={agent.is_enabled ? "success" : "neutral"}>{t(agent.is_enabled ? "agents.enabled" : "agents.disabled")}</Badge>
        ) : (
          <Badge tone="neutral">{t("agents.notRegistered")}</Badge>
        )}
      </div>
      <p className="text-[13px] text-ink-soft">{t(`agents.about.${code}`)}</p>
      {agent ? <AgentSettings agent={agent} /> : <RegisterAgent code={code} />}
    </Card>
  );
}

function RegisterAgent({ code }: { code: AgentCode }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canManage = useCan("core.agent.manage");
  const members = useInfiniteQuery({ ...memberListQuery(), enabled: canManage });
  const candidates = (members.data?.pages.flatMap((p) => p.rows) ?? []).filter(
    (m) => !m.is_self && m.status === "active" && m.role_ids.length === 0 && m.kind !== "agent",
  );
  const [userId, setUserId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const register = useMutation({
    mutationFn: () => registerAgent(code, userId),
    onMutate: () => setError(null),
    onSuccess: () =>
      Promise.all([queryClient.invalidateQueries({ queryKey: agentKeys.all }), queryClient.invalidateQueries({ queryKey: userKeys.all })]),
    onError: (e) => setError(agentErrorMessage(t, e)),
  });
  if (!canManage) return <p className="text-[13px] text-ink-muted">{t("agents.askAdmin")}</p>;
  return (
    <div className="flex flex-col gap-2 text-[13px]">
      <ol className="list-decimal pl-5 text-ink-soft">
        <li>{t("agents.step1")}</li>
        <li>{t("agents.step2")}</li>
        <li>{t("agents.step3")}</li>
      </ol>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t("agents.account")}
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className={cn(inputClass, "w-72")}>
            <option value="">{candidates.length ? t("agents.chooseAccount") : t("agents.noCandidates")}</option>
            {candidates.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.full_name ? `${m.full_name} · ${m.email}` : m.email}
              </option>
            ))}
          </select>
        </label>
        <button type="button" disabled={!userId || register.isPending} onClick={() => register.mutate()} className={primaryButtonClass}>
          {register.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          {t("agents.register")}
        </button>
      </div>
      <ServerError message={error} />
    </div>
  );
}

function AgentSettings({ agent }: { agent: AgentRow }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const canManage = useCan("core.agent.manage");
  const runs = useQuery(agentRunsQuery(agent.id));
  const [enabled, setEnabled] = useState(agent.is_enabled);
  const [horizon, setHorizon] = useState(String(agent.settings.horizon_days ?? 14));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => updateAgent(agent.id, enabled, { ...agent.settings, horizon_days: Number(horizon) }),
    onMutate: () => setError(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.agents() }),
    onError: (e) => setError(agentErrorMessage(t, e)),
  });
  const dirty = enabled !== agent.is_enabled || Number(horizon) !== (agent.settings.horizon_days ?? 14);
  const runTone = { running: "info", succeeded: "success", failed: "danger", skipped: "neutral" } as const;

  return (
    <div className="flex flex-col gap-3 text-[13px]">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={enabled} disabled={!canManage} onChange={(e) => setEnabled(e.target.checked)} />
          {t("agents.enable")}
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t("agents.horizon")}
          <input
            type="number"
            min={1}
            max={90}
            value={horizon}
            disabled={!canManage}
            onChange={(e) => setHorizon(e.target.value)}
            className={cn(inputClass, "tabular w-28 text-right")}
          />
        </label>
        {canManage && (
          <button type="submit" disabled={!dirty || save.isPending} className={primaryButtonClass}>
            {save.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t("form.save")}
          </button>
        )}
      </form>
      <ServerError message={error} />
      <section className="flex flex-col gap-1">
        <h3 className="font-semibold">{t("agents.lastRuns")}</h3>
        {(runs.data ?? []).length === 0 ? (
          <p className="text-ink-muted">{t("agents.noRuns")}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {(runs.data ?? []).map((r) => (
              <li key={r.id} className="flex items-center gap-2 py-1.5">
                <span className="tabular w-36 text-ink-muted">{fmt.dateTime(r.started_at)}</span>
                <Badge tone={runTone[r.status]}>{t(`agents.run.${r.status}`)}</Badge>
                <span className="truncate text-ink-soft">
                  {t(`agents.trigger.${r.trigger}`)}
                  {r.error && ` — ${r.error}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
